/**
 * ServerLoadMonitor — compact RAM/CPU/Disk indicators for the board
 * header right-side. Polls /api/system/load every 10 seconds.
 *
 * Visual contract (UI_GUIDELINES_v1 §2.3):
 *   - Idle (<70%) → muted gray
 *   - Stressed (70–90%) → yellow (status.warning)
 *   - Critical (>90%) → red (status.error)
 *
 * Layout: three pill-thin rows, each `LABEL  N% [████░░░░]`. The bar
 * is 48px wide so the whole monitor stays under ~360px and fits next
 * to the board title even on a narrow viewport.
 */
import { useQuery } from '@tanstack/react-query';

import { systemApi, systemKeys } from '../../lib/api';

type Tone = 'idle' | 'warning' | 'critical';

function toneFor(pct: number | null | undefined): Tone {
  if (pct == null) return 'idle';
  if (pct >= 90) return 'critical';
  if (pct >= 70) return 'warning';
  return 'idle';
}

function toneClass(tone: Tone): string {
  switch (tone) {
    case 'critical':
      return 'text-error';
    case 'warning':
      return 'text-warning';
    default:
      return 'text-muted-foreground';
  }
}

function barColor(tone: Tone): string {
  switch (tone) {
    case 'critical':
      return 'hsl(var(--color-error))';
    case 'warning':
      return 'hsl(var(--color-warning))';
    default:
      return 'hsl(var(--color-foreground) / 0.4)';
  }
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  const gb = bytes / 1024 / 1024 / 1024;
  return gb >= 1024 ? `${(gb / 1024).toFixed(1)} TB` : `${gb.toFixed(1)} GB`;
}

export function ServerLoadMonitor() {
  const q = useQuery({
    queryKey: systemKeys.load(),
    queryFn: () => systemApi.load(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  return (
    <div className="flex items-center gap-3 text-[11px] tabular-nums">
      <Metric
        label="RAM"
        pct={q.data?.ram.usedPct}
        tooltip={`Total ${formatBytes(q.data?.ram.totalBytes ?? null)}`}
      />
      <Metric label="CPU" pct={q.data?.cpu.usedPct} />
      <Metric
        label="DISK"
        pct={q.data?.disk.usedPct}
        tooltip={`Total ${formatBytes(q.data?.disk.totalBytes ?? null)}`}
      />
    </div>
  );
}

function Metric({
  label,
  pct,
  tooltip,
}: {
  label: string;
  pct: number | null | undefined;
  tooltip?: string;
}) {
  const tone = toneFor(pct);
  const text = pct == null ? '—' : `${Math.round(pct)}%`;
  return (
    <div className="flex items-center gap-1.5" title={tooltip}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/80">{label}</span>
      <span className={`min-w-[28px] text-right ${toneClass(tone)}`}>{text}</span>
      <div
        role="progressbar"
        aria-valuenow={pct ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} ${text}`}
        className="h-1 w-12 overflow-hidden rounded-full bg-foreground/10"
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct == null ? 0 : Math.min(100, Math.max(0, pct))}%`,
            backgroundColor: barColor(tone),
          }}
        />
      </div>
    </div>
  );
}
